import { type ServerResponse, axios } from '~/axios'

interface Organization {
  id: number
  name: string
  application: { id: number; name: string }
}

interface FamilyMember {
  memberIdentifier: string
  firstParticipant: boolean
  firstName: string
  lastName: string
  gender: string
  genderText: string
  customGender: string | null
  birthDate: number | null
  documentType: string
  documentTypeText: string
  customDocumentType: string | null
  documentNumber: string
  birthCountry: string
  email: string | null
  phoneNumber: string | null
  phoneCode: string | null
}

interface SnapshotEconomic {
  codeName: string
  value: string | null
  multipleValueArray: string[] | null
  questionText: string
  text: string | null
  multipleText: string[] | null
  multipleTextArray: string[] | null
  other: string | null
  topic: string
}

interface MemberEconomic {
  memberIdentifier: string
  firstName: string
  economic: SnapshotEconomic[]
}

interface IndicatorSurveyData {
  value: number
  shortName: string
  dimension: string
  key: string
  snapshotStoplightId: number
}

interface SnapshotIndicator {
  createdAt: number
  stoplightSkipped: boolean
  surveyId: number
  indicatorSurveyDataList: IndicatorSurveyData[]
  priorities: Array<{ key: string }>
  achievements: Array<{ key: string }>
  countRedIndicators: number
  countYellowIndicators: number
  countGreenIndicators: number
  countSkippedIndicators: number
  countDoesNotApplyIndicators: number
  countIndicatorsAchievements: number
  countIndicatorsPriorities: number
  indicatorsPriorities: Array<{ indicator: string }>
}

export interface Family {
  user: { userId: number; username: string }
  familyId: number
  name: string
  code: string
  latitude: string
  longitude: string
  numberOfSnapshots: number
  lastSnapshot: number
  allowRetake: boolean
  organization: Organization
  country: { country: string }
  project: null | { title: string; id: number }
  familyMemberDTOList: FamilyMember[]
  snapshotEconomics: SnapshotEconomic[]
  membersEconomic: MemberEconomic[]
  snapshotIndicators: SnapshotIndicator[]
}

/**
 * Fetches a family by its ID
 * @param familyId - The numeric ID of the family
 * @returns The family data
 */
export async function getFamilyById(familyId: number): Promise<Family> {
  const query = /* GraphQL */ `
    query familyById($id: Long) {
      familyById(id: $id) {
        user {
          userId
          username
        }
        familyId
        name
        code
        latitude
        longitude
        numberOfSnapshots
        lastSnapshot
        allowRetake
        organization {
          id
          name
          application {
            id
            name
          }
        }
        country {
          country
        }
        project {
          id
          title
        }
        familyMemberDTOList {
          memberIdentifier
          firstParticipant
          firstName
          lastName
          gender
          genderText
          customGender
          birthDate
          documentType
          documentTypeText
          customDocumentType
          documentNumber
          birthCountry
          email
          phoneNumber
          phoneCode
        }
        snapshotEconomics {
          codeName
          value
          multipleValueArray
          questionText
          text
          multipleText
          multipleTextArray
          other
          topic
        }
        membersEconomic {
          memberIdentifier
          firstName
          economic {
            codeName
            value
            multipleValue
            multipleValueArray
            questionText
            text
            multipleText
            multipleTextArray
            other
            topic
          }
        }
        snapshotIndicators {
          createdAt
          stoplightSkipped
          surveyId
          indicatorSurveyDataList {
            value
            shortName
            dimension
            key
            snapshotStoplightId
          }
          priorities {
            key
          }
          achievements {
            key
          }
          countRedIndicators
          countYellowIndicators
          countGreenIndicators
          countSkippedIndicators
          countDoesNotApplyIndicators
          countIndicatorsAchievements
          countIndicatorsPriorities
          indicatorsPriorities {
            indicator
          }
        }
      }
    }
  `

  const { data } = await axios.post<ServerResponse<{ familyById: Family }>>(
    '/graphql',
    {
      query,
      variables: { id: familyId },
    },
  )

  return data.data.familyById
}
